import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The two ways an error is shown inside a form.
 *
 * Replaces the four competing treatments in the codebase:
 * `bg-destructive/10 border border-destructive/20`, `text-red-500 text-xs`,
 * `text-sm text-red-600`, and a raw red paragraph. `FormError` is the boxed
 * summary at the top of a form or dialog; `FieldError` is the line under a
 * single field.
 *
 * Both render nothing when there is no message, so callers can pass a possibly
 * empty value without a guard.
 */

interface FormErrorProps {
  children?: ReactNode;
  className?: string;
}

/** Boxed summary: one per form or dialog, above the fields or in the footer. */
export function FormError({ children, className }: FormErrorProps) {
  if (children === null || children === undefined || children === false || children === "") {
    return null;
  }
  return (
    <div
      className={cn("rounded-md bg-red-50 px-3 py-2 text-sm text-red-700", className)}
      role="alert"
    >
      {children}
    </div>
  );
}

interface FieldErrorProps {
  message?: string | null;
  /** Match the field's `aria-describedby` so screen readers announce it. */
  id?: string;
  className?: string;
}

/** One line under a single field. */
export function FieldError({ message, id, className }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p id={id} className={cn("mt-1 text-sm text-red-600", className)} role="alert">
      {message}
    </p>
  );
}
